<template>
    <custom-table
        :title="$t('pages.ecommerce.subscription.title')"
        :subTitle="$t('pages.ecommerce.subscription.subTitle')"
        rowKey="id"
        :items="table.data"
        :loading="table.loading"
        selectableRows
        :columns="fields"
        :actions="actions"
        :pagination="table.pagination"
        :sortMeta="table.pagination.sort || []"
        changeUrlPage
        filterComponentName="SubscriptionFilter"
        @action="handleClickAction"
        @selectableRows="handleSelectedRow"
        @changeTable="handleTableChange"
        @sort="handleSort"
    >
        <template v-slot:subscriber="{ row: record }">
            <div class="d-flex flex-column">
                <span class="text-muted fs-8">{{ $t('pages.ecommerce.subscription.subscribable_types.' + record.subscribable_type) }}</span>
                <span class="fw-bold">{{ formatSubscribableName(record) }}</span>
            </div>
        </template>
        <template v-slot:product="{ row: record }">
            <div class="d-flex flex-column">
                <span class="fw-bold">{{ record.product?.name || '-' }}</span>
                <span v-if="record.product?.code" class="text-muted fs-8">{{ record.product.code }}</span>
            </div>
        </template>
        <template v-slot:price="{ row: record }">
            <template v-if="record.product_price">
                {{ formatPrice(record.product_price.price) }}
                <span class="text-muted">{{ record.product_price.currency?.code }}</span>
            </template>
            <template v-else>-</template>
        </template>
        <template v-slot:status="{ row: record }">
            <span :class="statusBadgeClass(record.status_id)" class="badge">{{ statusLabel(record.status_id) }}</span>
        </template>
        <template v-slot:expires_at="{ row: record }">
            {{ record.expires_at ? $moment(record.expires_at).format("DD.MM.YYYY") : '-' }}
        </template>
        <template v-slot:auto_renew="{ row: record }">
            <span :class="record.auto_renew ? 'badge-light-success' : 'badge-secondary'" class="badge">
                {{ record.auto_renew ? $t('common.yes') : $t('common.no') }}
            </span>
        </template>
        <template v-slot:created_at="{ row: record }">
            {{ record.created_at ? $moment(record.created_at).format("DD.MM.YYYY HH:mm") : '-' }}
        </template>
        <template v-slot:actions="{ row: record }">
            <div class="d-flex justify-content-end">
                <el-tooltip :content="$t('common.edit')" placement="top">
                    <router-link :to="'/ecommerce/subscription/save/' + record.id" class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1">
                        <span class="svg-icon svg-icon-3">
                            <inline-svg src="/media/icons/duotune/art/art005.svg"/>
                        </span>
                    </router-link>
                </el-tooltip>
                <a @click="deleteRecord([record.id])" class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm">
                    <span class="svg-icon svg-icon-3">
                        <inline-svg src="/media/icons/duotune/general/gen027.svg"/>
                    </span>
                </a>
            </div>
        </template>
    </custom-table>
</template>

<script>
import CustomTable from "@/components/custom-table";

const STATUS_CODE_MAP = {
    0: 'PENDING_PAYMENT',
    1: 'ACTIVE',
    2: 'EXPIRED',
    3: 'CANCELLED',
    4: 'SUSPENDED',
};

const STATUS_BADGE_MAP = {
    0: 'badge-light-warning',
    1: 'badge-light-success',
    2: 'badge-light-danger',
    3: 'badge-light-dark',
    4: 'badge-light-warning',
};

export default {
    name: "SubscriptionIndex",
    components: {CustomTable},
    data() {
        return {
            fields: [
                {name: this.$t("pages.ecommerce.subscription.cols.id"), key: "id", sortable: true},
                {name: this.$t("pages.ecommerce.subscription.cols.subscriber"), scopedSlots: {customRender: "subscriber"}},
                {name: this.$t("pages.ecommerce.subscription.cols.product"), sortingField: "product_id", sortable: true, scopedSlots: {customRender: "product"}},
                {name: this.$t("pages.ecommerce.subscription.cols.price"), scopedSlots: {customRender: "price"}},
                {name: this.$t("pages.ecommerce.subscription.cols.status"), sortingField: "status_id", sortable: true, scopedSlots: {customRender: "status"}},
                {name: this.$t("pages.ecommerce.subscription.cols.expires_at"), sortingField: "expires_at", sortable: true, scopedSlots: {customRender: "expires_at"}},
                {name: this.$t("pages.ecommerce.subscription.cols.auto_renew_short"), sortingField: "auto_renew", sortable: true, scopedSlots: {customRender: "auto_renew"}},
                {name: this.$t("common.createdAtShort"), sortingField: "created_at", sortable: true, scopedSlots: {customRender: "created_at"}},
                {name: this.$t("common.action"), key: "action", scopedSlots: {customRender: "actions"}},
            ],
            actions: [
                {name: "new", icon: "bi-plus-lg", label: "btn.add"},
                {name: "refresh", icon: "bi-arrow-repeat", label: "btn.refresh"},
                {name: "delete", icon: "bi-trash", label: "btn.delete"},
            ],
            selectedRowKeys: [],
            filterParameters: {},
        }
    },
    computed: {
        table() {
            return this.$store.state.ecommerce.subscription.table;
        },
    },
    mounted() {
        this.setCurrentPageBreadcrumbs(this.$t("menu.subscriptionList"), []);

        this.filterParameters = {
            sort: 'created_at:desc',
        };

        this.$store.dispatch('ecommerce/subscription/get', {
            page: {current: this.$route.query.page},
            filterData: Object.assign(this.filterParameters, this.$root.getFilterWithUrl({})),
        });
    },
    methods: {
        statusLabel(statusId) {
            return this.$t('pages.ecommerce.subscription.statuses.' + (STATUS_CODE_MAP[statusId] || 'PENDING_PAYMENT'));
        },
        statusBadgeClass(statusId) {
            return STATUS_BADGE_MAP[statusId] || 'badge-light-secondary';
        },
        formatPrice(price) {
            const value = parseFloat(price);
            if (isNaN(value)) return price;
            return value.toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2});
        },
        formatSubscribableName(record) {
            const sub = record.subscribable;
            if (!sub) return '#' + record.subscribable_id;
            if (record.subscribable_type === 'COMPANY') {
                return sub.name || sub.short_name || ('#' + sub.id);
            }
            const fn = sub.firstname || '';
            const ln = sub.lastname || '';
            return (fn + ' ' + ln).trim() || sub.email || ('#' + sub.id);
        },
        handleClickAction(name) {
            switch (name) {
                case "refresh":
                    this.refreshTable();
                    break;
                case "new":
                    this.$router.push({path: "/ecommerce/subscription/save"});
                    break;
                case "delete":
                    this.deleteRecord(this.selectedRowKeys.flat());
                    break;
            }
        },
        refreshTable() {
            this.$store.dispatch('ecommerce/subscription/get', {
                page: {current: this.$route.query.page},
                filterData: Object.assign(this.filterParameters, this.$root.getFilterWithUrl({})),
            });
        },
        handleTableChange(pagination, filterData) {
            const currentSort = this.filterParameters.sort || 'created_at:desc';
            this.filterParameters = {...filterData, sort: currentSort};

            this.$store.dispatch("ecommerce/subscription/get", {
                page: pagination,
                filterData: this.filterParameters,
            });
        },
        handleSort({columnName, order}) {
            this.filterParameters = {
                ...this.filterParameters,
                sort: `${columnName}:${order}`,
            };

            this.$store.dispatch("ecommerce/subscription/get", {
                page: {current: this.$route.query.page},
                filterData: this.filterParameters,
            });
        },
        handleSelectedRow(record) {
            this.selectedRowKeys = record;
        },
        deleteRecord(id) {
            this.eventBus.$emit('showConfirmDialog', {
                title: this.$t('btn.delete'),
                message: this.$t('messages.sureDelete'),
                confirmType: 'danger',
                onConfirm: () => {
                    this.$store.dispatch("ecommerce/subscription/delete", {id}).then((successDeleted) => {
                        this.selectedRowKeys = this.selectedRowKeys.filter(x => !successDeleted.includes(x));
                        this.refreshTable();
                    });
                },
            });
        },
    }
}
</script>
